// Routine actions (spec 4.4). Same rules as the server (server/src/sync/mutations.ts), applied locally first.
// Placing routines on days is the server's job (L35); the device only shows and checks off.

import { useLiveQuery } from "dexie-react-hooks";
import { weekStart } from "@shared/routines";
import { addDays, planningDate, timeToMinutes } from "@shared/time";
import { uuidv7 } from "@shared/uuid";
import type { MutationArgs } from "@shared/sync";
import { db, getMeta, type PlanEntry, type Routine, type RoutineCompletion } from "./db";
import type { Me } from "./session";
import { sync } from "./sync";

const now = () => new Date().toISOString();

async function today(): Promise<string> {
  const me = await getMeta<Me>("me");
  return planningDate(new Date(), timeToMinutes(me?.settings?.day_starts_at ?? "04:00"));
}

export function useRoutines(): Routine[] | undefined {
  return useLiveQuery(async () =>
    (await db.routines.toArray()).filter((r) => !r.deleted_at && !r.archived_at).sort((a, b) => a.position - b.position),
  );
}

/** Check offs of the weeks around a date (enough for counters, "Nog deze week" and the af en toe list). */
export function useCompletions(): RoutineCompletion[] | undefined {
  return useLiveQuery(async () => (await db.routine_completions.toArray()).filter((c) => !c.deleted_at));
}

/** Routine entries of the week of a date, for "Nog deze week". */
export function useWeekRoutineEntries(date: string): PlanEntry[] | undefined {
  const from = weekStart(date);
  return useLiveQuery(
    () =>
      db.plan_entries
        .where("plan_date")
        .between(from, addDays(from, 6), true, true)
        .filter((e) => e.kind === "routine" && !e.deleted_at)
        .toArray(),
    [from],
  );
}

function completionRow(
  id: string,
  routine: Pick<Routine, "id" | "space_id" | "user_id">,
  date: string,
  entryId: string | null,
  at: string,
): RoutineCompletion {
  return {
    id,
    space_id: routine.space_id,
    user_id: routine.user_id,
    routine_id: routine.id,
    done_on: date,
    plan_entry_id: entryId,
    completed_at: at,
    version: 0,
    deleted_at: null,
    created_at: at,
    updated_at: at,
  };
}

async function liveCompletion(routineId: string, date: string) {
  return db.routine_completions
    .where("routine_id")
    .equals(routineId)
    .filter((c) => !c.deleted_at && c.done_on === date)
    .first();
}

/**
 * Checked off outside a day (Nog deze week, routine list, af en toe). Returns the check off id for undo,
 * or null when it was done that day already. Done early takes the next time this week off (RO2 AC5).
 */
export async function completeRoutine(routine: Routine, date?: string): Promise<string | null> {
  const day = date ?? (await today());
  if (await liveCompletion(routine.id, day)) return null;
  const id = uuidv7();
  const at = now();
  const entry = await db.plan_entries
    .where("routine_id")
    .equals(routine.id)
    .filter((e) => e.plan_date === day && e.status === "planned" && !e.deleted_at)
    .first();
  let early: PlanEntry | undefined;
  if (!entry && routine.schedule_type === "cadence") {
    const current = await today();
    const sunday = addDays(weekStart(day), 6);
    const later = await db.plan_entries
      .where("routine_id")
      .equals(routine.id)
      .filter((e) => e.status === "planned" && !e.deleted_at && e.plan_date > day && e.plan_date <= sunday && e.plan_date > current)
      .sortBy("plan_date");
    for (const e of later) {
      const plan = await db.day_plans.get(e.day_plan_id);
      if (plan?.status === "generated") {
        early = e;
        break;
      }
    }
  }
  const rows = [id, ...(entry ? [entry.id] : []), ...(early ? [early.id] : [])];
  await sync.mutate("routine.complete", { id, routineId: routine.id, date: day, at }, rows, async () => {
    if (entry) await db.plan_entries.update(entry.id, { status: "done", completed_at: at });
    await db.routine_completions.add(completionRow(id, routine, day, entry?.id ?? null, at));
    if (early) await db.plan_entries.update(early.id, { deleted_at: at });
  });
  return id;
}

export async function uncompleteRoutine(completionId: string): Promise<void> {
  const completion = await db.routine_completions.get(completionId);
  if (!completion || completion.deleted_at) return;
  const at = now();
  // Undo of "done early": the time taken off a later day at the same moment comes back (same rule as the server).
  const current = await today();
  const removed = completion.plan_entry_id
    ? []
    : await db.plan_entries
        .where("routine_id")
        .equals(completion.routine_id)
        .filter((e) => !!e.deleted_at && Date.parse(e.deleted_at) === Date.parse(completion.completed_at) && e.plan_date > current)
        .toArray();
  const rows = [completionId, ...(completion.plan_entry_id ? [completion.plan_entry_id] : []), ...removed.map((e) => e.id)];
  await sync.mutate("routine.uncomplete", { id: completionId, at }, rows, async () => {
    await db.routine_completions.update(completionId, { deleted_at: at });
    for (const e of removed) await db.plan_entries.update(e.id, { deleted_at: null });
    if (completion.plan_entry_id) {
      const entry = await db.plan_entries.get(completion.plan_entry_id);
      if (entry?.status === "done") await db.plan_entries.update(entry.id, { status: "planned", completed_at: null });
    }
  });
}

/** A routine checked where it stands on Vandaag: the entry and its check off go together (L30). */
export async function setRoutineEntryDone(entry: PlanEntry, done: boolean): Promise<void> {
  if (!entry.routine_id) return;
  if (done && entry.plan_date > (await today())) return;
  const at = now();
  const routine = await db.routines.get(entry.routine_id);
  const existing = await liveCompletion(entry.routine_id, entry.plan_date);
  const completionId = done ? (existing?.id ?? uuidv7()) : undefined;
  const rows = [entry.id, ...(existing ? [existing.id] : completionId ? [completionId] : [])];
  await sync.mutate("planEntry.setDone", { id: entry.id, done, at, ...(completionId ? { completionId } : {}) }, rows, async () => {
    await db.plan_entries.update(entry.id, done ? { status: "done", completed_at: at } : { status: "planned", completed_at: null });
    if (done && !existing && routine)
      await db.routine_completions.add(completionRow(completionId!, routine, entry.plan_date, entry.id, at));
    if (!done && existing) await db.routine_completions.update(existing.id, { deleted_at: at });
  });
}

export type RoutineForm = Omit<MutationArgs<"routine.save">, "id">;

export async function saveRoutine(form: RoutineForm, id?: string): Promise<string> {
  const routineId = id ?? uuidv7();
  const me = await getMeta<Me>("me");
  const existing = id ? await db.routines.get(id) : undefined;
  const at = now();
  const fields = {
    title: form.title.trim(),
    schedule_type: form.scheduleType,
    weekdays: form.scheduleType === "fixed" ? [...new Set(form.weekdays)].sort() : [],
    times_per_week: form.scheduleType === "cadence" ? form.timesPerWeek : null,
    interval_days: form.scheduleType === "occasional" ? form.intervalDays : null,
    slot_kinds: form.slotKinds,
    duration_minutes: form.durationMinutes,
    weekly_target: form.scheduleType === "fixed" ? (form.weeklyTarget ?? null) : null,
    is_optional: form.isOptional,
    last_done_on: form.lastDoneOn,
  };
  await sync.mutate("routine.save", { id: routineId, ...form, title: fields.title }, [routineId], async () => {
    if (existing) {
      await db.routines.update(routineId, fields);
    } else {
      const position = Math.max(-1, ...(await db.routines.toArray()).map((r) => r.position)) + 1;
      await db.routines.add({
        id: routineId,
        space_id: me?.settings?.personal_space_id ?? "",
        user_id: me?.user.id ?? "",
        ...fields,
        dismissed_week: null,
        position,
        archived_at: null,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    }
  });
  return routineId;
}

export async function archiveRoutine(id: string): Promise<void> {
  const at = now();
  await sync.mutate("routine.archive", { id, at }, [id], async () => {
    await db.routines.update(id, { archived_at: at });
  });
}

/** "Opnieuw beginnen" for "Nog deze week": these routines rest until Monday. Null undoes it. */
export async function setDismissedWeek(ids: string[], week: string | null): Promise<void> {
  if (!ids.length) return;
  await sync.mutate("routines.setDismissedWeek", { ids, week }, ids, async () => {
    await db.routines.bulkUpdate(ids.map((key) => ({ key, changes: { dismissed_week: week } })));
  });
}

/** The last day of the week of `date` that the app has made (for "Nog deze week", L71). */
export function usePlannedThrough(date: string): string | null | undefined {
  const from = weekStart(date);
  return useLiveQuery(async () => plannedThrough(from), [from]);
}

export async function plannedThrough(monday: string): Promise<string | null> {
  const plans = await db.day_plans
    .where("plan_date")
    .between(monday, addDays(monday, 6), true, true)
    .filter((p) => !p.deleted_at)
    .toArray();
  return plans.reduce<string | null>((max, p) => (!max || p.plan_date > max ? p.plan_date : max), null);
}

/** "Morgen geen gym": off that day, the week goal stays (B24); a sport block with nothing else in it goes along. */
/** The sport block that goes along with a gym entry: when nothing else is planned in it. */
async function sportAlongside(entry: PlanEntry) {
  const block = entry.day_block_id ? await db.day_blocks.get(entry.day_block_id) : undefined;
  const others = block
    ? (await db.plan_entries.toArray()).some(
        (e) => e.day_block_id === block.id && e.id !== entry.id && e.status === "planned" && !e.deleted_at,
      )
    : true;
  return block?.slot_kind === "sport" && !others ? block : undefined;
}

/**
 * "Morgen geen gym" (B24) or back on. Back on in a full week takes `swap`: that planned gym day of the same week goes
 * off first (B28).
 */
export async function skipRoutineEntry(entry: PlanEntry, skipped: boolean, swap?: PlanEntry): Promise<void> {
  const alongside = await sportAlongside(entry);
  const swapAlong = swap ? await sportAlongside(swap) : undefined;
  const rowIds = [entry.id, ...(alongside ? [alongside.id] : []), ...(swap ? [swap.id] : []), ...(swapAlong ? [swapAlong.id] : [])];
  await sync.mutate(
    "routineEntry.skip",
    { id: entry.id, skipped, swapEntryId: swap?.id, today: planningDate(new Date()) },
    rowIds,
    async () => {
      if (swap) await db.plan_entries.update(swap.id, { status: "skipped" });
      if (swapAlong?.status === "open") await db.day_blocks.update(swapAlong.id, { status: "skipped" });
      await db.plan_entries.update(entry.id, { status: skipped ? "skipped" : "planned" });
      if (alongside && skipped && alongside.status === "open") await db.day_blocks.update(alongside.id, { status: "skipped" });
      if (alongside && !skipped && alongside.status === "skipped") await db.day_blocks.update(alongside.id, { status: "open" });
    },
  );
}
